import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { refreshFeedData } from "../../services/functions";
import { getNotificationListUnread, fetchUnreadCount } from "../../redux/actions";
import {
  AUTO_REFRESH_CONFIG,
  shouldEnableAutoRefresh,
  isUserAuthenticated,
} from "../../settings/autoRefreshConfig";
import { clearCalendarCache } from "../../services/calendarCache";
import { invalidateTrialInfoCache } from "../../services/trialInfoCache";
import { resetCourseThrottle } from "../../redux/actions/courseActions";
import { resetUserInfoThrottle } from "../../redux/actions/userActions";
import { getAuthPath } from "../../services/RouteAuth";
import { getUserInfo, fetchAppPermissions } from "../../redux/actions";
import {
  markOnboardingPauseIfApplicable,
  clearOnboardingPauseMarker,
  consumeOnboardingForceQuitIfPending,
} from "../../utils/onboardingForceQuitGuard";
import dayjs from "dayjs";

/**
 * Shortest gap between two subscription re-reads on resume. Long enough that
 * flicking between apps doesn't hammer the profile endpoint, short enough that
 * returning from a payment sheet always re-reads.
 */
const SUBSCRIPTION_RECHECK_MS = 20_000;

/**
 * Global app state manager component
 * Handles app foreground/background state changes, auto-refresh,
 * and critical data refresh on foreground return (merged from FirstLoad).
 */
const AppStateManager = ({
  enabled = true,
  refreshInterval = 2,
  from_main_group = true,
  group_id = null,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const lastRefreshTime = useRef(null);
  const isActive = useRef(true);
  const isInitialized = useRef(false);
  const lastInactiveTime = useRef(null);
  const didBootFetchRef = useRef(false);
  const lastSubscriptionCheck = useRef(null);

  const unreadNotificationsList = useSelector(
    (state) => state.notifications.getNotificationListUnread,
  );

  // ── Force-quit guard ────────────────────────────────────────────────
  // Runs exactly once on app cold start (before the pathname-watch
  // effect re-stamps the marker for the current route). If the prior
  // session was killed mid-onboarding, this redirects to /logout.
  // NOTE: must NOT depend on location.pathname — the pathname watcher
  // below re-sets the marker on every onboarding route render, so
  // re-running consume on path change would mis-trigger a logout the
  // first time the user advances between onboarding steps.
  useEffect(() => {
    consumeOnboardingForceQuitIfPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Initial load (was FirstLoad) ────────────────────────────────────
  // Fires the first time getAuthPath() resolves to "full" in this
  // session. Previously this ran once on mount with the boot URL
  // (typically "/", which is "public") so the gate skipped and the
  // permissions slice never hydrated — every gated tap then silently
  // no-op'd because the hook saw isLoaded=false → reason "unknown"
  // → no navigate, no popup.
  useEffect(() => {
    if (didBootFetchRef.current) return;
    if (getAuthPath() !== "full") return;

    didBootFetchRef.current = true;
    dispatch(getUserInfo());
    dispatch(fetchAppPermissions());
    if (unreadNotificationsList.data === null) {
      dispatch(getNotificationListUnread());
    }
    dispatch(fetchUnreadCount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ── Onboarding pause marker — pathname watcher ──────────────────────
  // Maintain the force-quit marker in real time as the user navigates.
  // We can't rely on Capacitor/visibility lifecycle events to set it
  // just-in-time before a kill — iOS in particular doesn't always fire
  // those before the app dies from the app switcher. By stamping the
  // marker the moment the user lands on an onboarding URL (and dropping
  // it the moment they leave), localStorage already reflects the truth
  // when the OS pulls the plug. Cold-start consume reads the marker
  // (not window.location, which Android resets to "/"), so this works
  // on both platforms on the first force-quit attempt.
  useEffect(() => {
    if (location.pathname.startsWith("/impostazioni-iniziali/")) {
      markOnboardingPauseIfApplicable();
    } else {
      clearOnboardingPauseMarker();
    }
  }, [location.pathname]);

  // Function to get current path safely
  const getCurrentPath = () => {
    try {
      return window.location.pathname;
    } catch (error) {
      return "/home";
    }
  };

  // Function to handle app becoming active
  const handleAppActive = () => {
    // Note: the onboarding force-quit marker is no longer touched
    // here. The pathname-watcher useEffect above is the single source
    // of truth — it sets the marker while the user is on any
    // /impostazioni-iniziali/* URL and clears it the moment they
    // navigate elsewhere. Touching it from resume/pause handlers
    // would race the watcher (e.g. clearing it on resume even though
    // the user is still mid-onboarding would silently disable the
    // guard for the next force-quit).
    if (!isActive.current && enabled) {
      isActive.current = true;

      // Check if user is authenticated
      if (
        AUTO_REFRESH_CONFIG.CONDITIONS.REQUIRE_AUTH &&
        !isUserAuthenticated()
      ) {
        return;
      }

      const now = dayjs();

      // ── Subscription state ──────────────────────────────────────────
      // Re-read on EVERY return to the foreground, before and independent of
      // the inactivity threshold below.
      //
      // A store purchase backgrounds the app only for as long as the payment
      // sheet is up — often well under a minute — so gating this behind the
      // feed-refresh threshold meant the one resume that matters most, the one
      // right after paying, refreshed nothing. The user came back to an app
      // still showing its pre-purchase state, with the silent push as the only
      // other way to learn otherwise; when that push can't be delivered (a
      // stale FCM token, notifications declined) nothing corrected it until the
      // app was restarted. Two profile requests are a small price for never
      // showing someone a locked menu they have just paid to unlock.
      if (getAuthPath() === "full") {
        const sinceLastCheck =
          Date.now() - (lastSubscriptionCheck.current ?? 0);

        if (sinceLastCheck >= SUBSCRIPTION_RECHECK_MS) {
          lastSubscriptionCheck.current = Date.now();
          // Everything cached that is keyed on the subscription: the calendar's
          // day-availability, the trial/offer eligibility, the course throttle
          // and the profile dedup window. Leaving any of them in place would let
          // a stale pre-purchase answer outlive the refresh below.
          clearCalendarCache();
          invalidateTrialInfoCache();
          resetCourseThrottle();
          resetUserInfoThrottle();
          // The backend serves subscription status live (no cache), so this
          // alone updates the badge and the menu-locking gates — including a
          // subscription activated elsewhere, e.g. GUEST → active via the CRM.
          dispatch(getUserInfo());
          // Permission gates are cached client-side, so they need their own
          // reconcile or the pre-purchase gates persist until a cold start.
          // The optimistic shield in permissionsSlice.fetchAppPermissions
          // covers the case where the backend hasn't caught up yet.
          dispatch(fetchAppPermissions());
        }
      }

      // Check if app was inactive for minimum required time.
      // Measured in seconds: dayjs diff truncates, so a threshold expressed in
      // fractional minutes ("0.5") could never be met — every gap under a full
      // minute came back as 0 and was discarded.
      const lastInactive = lastInactiveTime.current
        ? dayjs(lastInactiveTime.current)
        : null;
      const inactiveSeconds = lastInactive ? now.diff(lastInactive, "second") : 0;

      if (
        inactiveSeconds <
        AUTO_REFRESH_CONFIG.CONDITIONS.MIN_INACTIVE_TIME * 60
      ) {
        return;
      }

      // ── Critical data refresh (was FirstLoad) ───────────────────────
      // Been away > threshold — data may be stale from another device
      if (getAuthPath() === "full") {
        dispatch(getNotificationListUnread());
        dispatch(fetchUnreadCount());
      }

      // ── Feed auto-refresh ───────────────────────────────────────────
      const currentPath = getCurrentPath();
      if (!shouldEnableAutoRefresh(currentPath)) {
        return;
      }

      const lastRefresh = lastRefreshTime.current
        ? dayjs(lastRefreshTime.current)
        : null;
      const shouldRefresh =
        !lastRefresh || now.diff(lastRefresh, "minute") >= refreshInterval;

      if (shouldRefresh) {
        refreshFeedData(dispatch, from_main_group, group_id);
        lastRefreshTime.current = now.format("YYYY-MM-DD HH:mm:ss");
      }
    }
  };

  // Function to handle app becoming inactive
  const handleAppInactive = () => {
    isActive.current = false;
    lastInactiveTime.current = dayjs().format("YYYY-MM-DD HH:mm:ss");
    // Note: not arming the onboarding pause marker here. The
    // pathname-watcher useEffect already keeps the marker in sync
    // with the current URL in real time, so it's correctly set
    // whenever the user is on an onboarding step — independent of
    // whether the OS gets a chance to fire any background lifecycle
    // event before killing the app. (iOS in particular doesn't
    // reliably deliver appStateChange isActive=false before a kill
    // from the app switcher, which is why this used to need 2-3
    // tries.)
  };

  useEffect(() => {
    if (!enabled || isInitialized.current) return;

    isInitialized.current = true;

    // Set initial refresh time
    lastRefreshTime.current = dayjs().format("YYYY-MM-DD HH:mm:ss");

    // Visibility change — primary foreground/background detection
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleAppInactive();
      } else {
        handleAppActive();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Capacitor native app state listener.
    //
    // The handle is kept so cleanup can remove THIS listener and nothing else.
    // It used to call the plugin's removeAllListeners(), which is app-wide: every
    // time `enabled` flipped (entering onboarding) it also silenced the funnel's
    // flush on background, deep links (appUrlOpen), the chat reconnect and every
    // other appStateChange listener — and nothing ever registered them again.
    let appStateHandle = null;
    let disposed = false;
    const appPlugin = window.Capacitor?.Plugins?.App;

    if (appPlugin) {
      try {
        Promise.resolve(
          appPlugin.addListener("appStateChange", ({ isActive: active }) => {
            if (active) {
              handleAppActive();
            } else {
              handleAppInactive();
            }
          }),
        )
          .then((handle) => {
            // Cleanup can run before the native bridge hands the handle back.
            if (disposed) {
              handle?.remove?.();
            } else {
              appStateHandle = handle;
            }
          })
          .catch(() => {});
      } catch {
        /* not running natively */
      }
    }

    // Cleanup
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      try {
        appStateHandle?.remove?.();
      } catch {
        /* already gone */
      }

      // Allow the effect to register again when it next runs (e.g. `enabled`
      // turning back on after onboarding). With the guard left set, the app
      // had no resume handling for the rest of the session.
      isInitialized.current = false;
    };
  }, [enabled, dispatch, from_main_group, group_id, refreshInterval]);

  return null;
};

export default AppStateManager;
